import React, { useState } from "react";
import { Copy, Check, AlertTriangle, Globe } from "lucide-react";
import "../styles/clients.css";

export type ApiKeyType = "production" | "development";

interface Props {
    apiKey: string;
    keyType?: ApiKeyType;
    clientName?: string;
    clientId?: string;
    allowedOrigins?: string[];
    onClose: () => void;
}

export default function ApiKeyModal({ apiKey, keyType, clientName, clientId, allowedOrigins, onClose }: Props) {
    const [copied, setCopied] = useState(false);
    const [copiedEmbed, setCopiedEmbed] = useState(false);

    const isProd = !keyType || keyType === "production";
    const keyLabel = isProd ? "Production API Key" : "Development API Key";

    const handleCopy = () => {
        navigator.clipboard.writeText(apiKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const embedCode = `<script>
(function(){
  var s=document.createElement('script');
  s.src='${window.location.origin}/embed.js?clientId=${clientId || "YOUR_CLIENT_ID"}&apiKey=${apiKey}&_t='+Date.now();
  s.async=true;
  document.body.appendChild(s);
})();
</script>`;

    const handleCopyEmbed = () => {
        navigator.clipboard.writeText(embedCode);
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card modal-card--wide" onClick={(e) => e.stopPropagation()}>
                <h2>{keyLabel} Generated</h2>
                {clientName && (
                    <p className="modal-subtitle">
                        {keyLabel} for <strong>{clientName}</strong>
                    </p>
                )}

                <div className="apikey-warning">
                    <AlertTriangle size={16} />
                    <span>Store this key securely — it cannot be retrieved again.</span>
                </div>

                <div className="guide-steps">
                    <div className="guide-step">
                        <div className="guide-step-number">1</div>
                        <div className="guide-step-content">
                            <h4>Save Your {keyLabel}</h4>
                            <p>Copy and store this key securely. You won't be able to see it again.</p>
                            <div className="apikey-display">
                                <code className="apikey-value">{apiKey}</code>
                                <button className="apikey-copy-btn" onClick={handleCopy}>
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                    {copied ? "Copied" : "Copy"}
                                </button>
                            </div>
                            {isProd && (
                                <p className="apikey-hint">Use this key in production. It will not work from localhost.</p>
                            )}
                            {!isProd && (
                                <p className="apikey-hint">Use this key for local development. It only works from localhost.</p>
                            )}
                        </div>
                    </div>

                    {allowedOrigins && allowedOrigins.length > 0 && (
                        <div className="guide-step">
                            <div className="guide-step-number">2</div>
                            <div className="guide-step-content">
                                <h4>Allowed Origins</h4>
                                <p>Your widget is restricted to these domains:</p>
                                <div className="apikey-origins">
                                    <div className="apikey-origins-list">
                                        {allowedOrigins.map((origin, i) => (
                                            <code key={i}>{origin}</code>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {clientId && isProd && (
                        <div className="guide-step">
                            <div className="guide-step-number">{allowedOrigins && allowedOrigins.length > 0 ? "3" : "2"}</div>
                            <div className="guide-step-content">
                                <h4>Add the Embed Snippet</h4>
                                <p>Paste this code before the closing <code>&lt;/body&gt;</code> tag of your website.</p>
                                <div className="embed-code-block">
                                    <pre><code>{embedCode}</code></pre>
                                    <button className="apikey-copy-btn embed-copy-btn" onClick={handleCopyEmbed}>
                                        {copiedEmbed ? <Check size={14} /> : <Copy size={14} />}
                                        {copiedEmbed ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    <button className="btn-modal-submit" onClick={onClose}>
                        I've saved it
                    </button>
                </div>
            </div>
        </div>
    );
}
